import React from "react";
import Disclosure from "../Disclosure";
import { formatCurrency } from "../../../utils/formatters";
import {
  LINKED_TIER_LABELS,
  LINKED_TIER_ORDER,
  LINKED_TIER_RULES,
} from "./rafterTailModel";
import "./rafter-tail.css";

const DEPARTMENTS = [
  ["cnc", "CNC"],
  ["cut", "Cut shop"],
  ["fab", "Fabrication"],
  ["finish", "Paint / stain"],
];

function hours(value) {
  const n = Number(value);
  return Number.isFinite(n) ? `${n} hr` : "—";
}

function sheetLine(part) {
  if (!part) return null;
  const [w, l] = part.sheet_in || [];
  const [bw, bl] = part.blank_in || [];
  return `${part.sheets_required} × ${w}×${l} in sheet${
    Number(part.sheets_required) === 1 ? "" : "s"
  } · ${part.pieces_per_sheet} per sheet · blanks ${bw}×${bl} in`;
}

function CostTile({ label, option, quantity, inQuote }) {
  return (
    <div className={`est-rt__cost${inQuote ? " est-rt__cost--on" : ""}`}>
      <div className="est-rt__cost-k">
        {label}
        {inQuote ? <span className="est-rt__badge">In this quote</span> : null}
      </div>
      <div className="est-rt__cost-v wb-num">
        {formatCurrency(option?.cost_per_unit)}
        <span className="est-rt__per"> per tail</span>
      </div>
      <div className="est-rt__cost-t wb-num">
        {formatCurrency(option?.cost)} for {quantity}
      </div>
    </div>
  );
}

/**
 * Cost and selling price for a rafter tail on the pricing-trial model.
 *
 * The two are kept visibly apart on purpose: the top block is what the tails
 * cost Island, the bottom block is what each kind of customer pays. Mixing
 * them up is the mistake the owner asked the screen to make impossible.
 */
function RafterTailTrialPanel({
  result,
  jobTier,
  withPaint,
  components = [],
  onRemoveComponent,
  onRepriceToday,
  ratesSaved = false,
}) {
  const orphans = (
    <>
      {components.length ? (
        <div className="est-alert est-rt__orphans" role="alert">
          <p>
            {components.length === 1
              ? "This component is not priced on the trial. Remove it and add it as its own line, or switch this line back to the rate card."
              : `These ${components.length} components are not priced on the trial. Remove them and add them as their own lines, or switch this line back to the rate card.`}
          </p>
          <ul>
            {components.map((name, index) => (
              <li key={`${name}-${index}`}>
                <span>{name}</span>
                <button
                  type="button"
                  className="wb-tbtn"
                  onClick={() => onRemoveComponent?.(index)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
  const linked = result?.linked_pricing || null;
  const working = result?.rafter_tail || null;

  if (!linked || !working || result?.error) {
    return (
      <fieldset className="est-fieldset">
        <legend>Rafter-tail pricing</legend>
        {orphans}
        <p className="wb-hint">
          Enter PVC or ACRE, width, projection, height and quantity to price
          these tails.
        </p>
      </fieldset>
    );
  }

  const quantity = Number(result?.input_data?.quantity) || 0;
  // Both options are always priced, so the table shows the one the Finish
  // control names right now. The badge marks what the job total holds —
  // they differ only until the edit reprices, and the note says so.
  const shownKey = withPaint ? "with_paint" : "without_paint";
  const selected = linked.options[shownKey];
  const repricing = shownKey !== linked.selected_option;
  const rates = linked.distributor_rates_used || {};
  const commission =
    Number(rates.commission_rate || 0) +
    Number(rates.shop_commission_rate || 0);

  return (
    <fieldset className="est-fieldset est-rt">
      <legend>Rafter-tail pricing</legend>

      {orphans}
      {ratesSaved || linked.distributor_rates_source === "saved_with_quote" ? (
        <div className="est-rt__saved">
          <p className="wb-hint">
            Distributor rates are locked to this quote, so a settings change
            never moves its price.
          </p>
          {onRepriceToday ? (
            <button type="button" className="wb-tbtn" onClick={onRepriceToday}>
              Reprice at today&apos;s rates
            </button>
          ) : null}
        </div>
      ) : null}
      {repricing ? (
        <p className="wb-hint" role="status">
          Repricing — the job total still holds the{" "}
          {linked.selected_option === "with_paint" ? "painted" : "unpainted"}{" "}
          price until the change is calculated.
        </p>
      ) : null}

      <p className="est-rt__eyebrow">Cost to Island</p>
      <div className="est-rt__costs">
        <CostTile
          label="Without paint"
          option={linked.options.without_paint}
          quantity={quantity}
          inQuote={linked.selected_option === "without_paint"}
        />
        <CostTile
          label="With paint"
          option={linked.options.with_paint}
          quantity={quantity}
          inQuote={linked.selected_option === "with_paint"}
        />
      </div>

      <p className="est-rt__eyebrow">
        Selling price · {withPaint ? "with paint" : "without paint"} · before
        tax
      </p>
      <div className="est-card">
        <table
          className="est-card__table est-rt__tiers"
          aria-label="Selling price by customer tier"
        >
          <thead>
            <tr>
              <th scope="col">Customer tier</th>
              <th scope="col">Per tail</th>
              <th scope="col">For {quantity}</th>
            </tr>
          </thead>
          <tbody>
            {LINKED_TIER_ORDER.map((tier) => {
              const price = selected?.tier_prices?.[tier];
              const isJobTier = tier === jobTier;
              return (
                <tr
                  key={tier}
                  className={isJobTier ? "est-rt__tier--on" : undefined}
                  aria-current={isJobTier ? "true" : undefined}
                >
                  <th scope="row">
                    <span className="est-rt__tier-name">
                      {LINKED_TIER_LABELS[tier]}
                      {isJobTier ? (
                        <span className="est-rt__badge">This estimate</span>
                      ) : null}
                    </span>
                    <span className="est-rt__tier-rule">
                      {LINKED_TIER_RULES[tier]}
                    </span>
                  </th>
                  <td className="wb-num">{formatCurrency(price?.per_unit)}</td>
                  <td className="wb-num">{formatCurrency(price?.total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="wb-hint">
        Distributor is cost plus {Number(rates.overhead_ratio)}% overhead, a{" "}
        {Number(rates.profit_margin)}% margin and {commission}% commission.
        Every other tier is set from it — nothing is added twice. Tax is added
        separately on the job.
      </p>

      <Disclosure
        className="est-rt__working"
        summary="How the cost was worked out"
        meta={null}
      >
        <dl className="est-rt__facts">
          <div>
            <dt>Material allowance</dt>
            <dd className="wb-num">
              {working.profile_sqft} sq ft × {working.width_factor} ={" "}
              {Number(working.factored_sqft)} sq ft ×{" "}
              {formatCurrency(working.material_rate_per_sqft)} ={" "}
              {formatCurrency(working.material_per_tail)} per tail
            </dd>
          </div>
          <div>
            <dt>Shop labor at {formatCurrency(working.labor_rate)}/hr</dt>
            <dd>
              <table className="est-rt__hours">
                <tbody>
                  {DEPARTMENTS.map(([key, label]) => (
                    <tr key={key}>
                      <th scope="row">{label}</th>
                      <td className="wb-num">{hours(working.hours?.[key])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <span className="est-rt__small">
                Each department&apos;s total rounded up to the next quarter
                hour. Paint hours count only with paint.
              </span>
            </dd>
          </div>
          <div>
            <dt>Paint / stain material</dt>
            <dd className="wb-num">
              {Number(working.finish_gallons)} gal ×{" "}
              {formatCurrency(working.finish_price_per_gallon)} ={" "}
              {formatCurrency(working.finish_material_total)}
            </dd>
          </div>
          <div>
            <dt>Side sheets (½ in)</dt>
            <dd className="wb-num">{sheetLine(working.purchasing?.sides)}</dd>
          </div>
          <div>
            <dt>Face skins (⅛ in)</dt>
            <dd className="wb-num">{sheetLine(working.purchasing?.faces)}</dd>
          </div>
        </dl>
        <p className="wb-hint">
          Sheet counts are for purchasing only — the cost uses the material
          allowance above, not whole sheets.
        </p>
      </Disclosure>
    </fieldset>
  );
}

export default RafterTailTrialPanel;
